import React, { useEffect, useState } from 'react';
import { hrmsAnalyticsOverview, hrmsAnalyticsFunnel, hrmsAnalyticsTat, hrmsAnalyticsSources, hrmsAnalyticsOffers } from '../../lib/hrmsApi';
import { Briefcase, Users, IdCard, FileText, Clock, TrendingUp, AlertTriangle } from 'lucide-react';

const Stat = ({ icon: I, label, value, hint, tone = 'orange' }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`h-9 w-9 rounded-full flex items-center justify-center bg-${tone}-50 text-${tone}-600`}><I className="h-4 w-4" /></div>
    </div>
    <div className="mt-2 text-2xl font-extrabold text-slate-900">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const AnalyticsPage = () => {
  const [ov, setOv] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [tat, setTat] = useState(null);
  const [sources, setSources] = useState(null);
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    (async () => {
      setOv(await hrmsAnalyticsOverview().catch(() => null));
      setFunnel(await hrmsAnalyticsFunnel().catch(() => null));
      setTat(await hrmsAnalyticsTat().catch(() => null));
      setSources(await hrmsAnalyticsSources().catch(() => null));
      setOffers(await hrmsAnalyticsOffers().catch(() => null));
    })();
  }, []);

  const max = funnel ? Math.max(...funnel.cumulative.map((c) => c.count), 1) : 1;

  return (
    <div data-testid="analytics-page">
      <h1 className="text-2xl font-extrabold">HR analytics</h1>
      <p className="text-sm text-slate-500 mt-1">Live view of recruitment funnel, time-to-hire, sources and offer economics.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Briefcase} label="Open jobs" value={ov?.jobs_open ?? '—'} hint={`${ov?.jobs_total ?? '—'} total`} />
        <Stat icon={Users} label="Applications" value={ov?.applications_total ?? '—'} tone="blue" />
        <Stat icon={IdCard} label="Employees" value={ov?.employees_total ?? '—'} tone="emerald" />
        <Stat icon={Clock} label="Interviews (upcoming)" value={ov?.interviews_upcoming ?? '—'} tone="amber" />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="analytics-funnel">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Recruitment funnel</h2>
            <span className="text-xs text-slate-500">{funnel?.total_applications ?? 0} applications</span>
          </div>
          <div className="mt-4 space-y-2">
            {(funnel?.cumulative || []).map((c) => {
              const pct = Math.round((c.count / max) * 100);
              return (
                <div key={c.stage}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 capitalize">{c.label}</span>
                    <span className="font-mono text-slate-900">{c.count}</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Biggest drop-offs</div>
            {(funnel?.drops || []).filter((d) => d.dropped > 0).sort((a, b) => b.dropped - a.dropped).slice(0, 4).map((d, i) => (
              <div key={i} className="flex justify-between text-xs py-1">
                <span className="text-slate-700 capitalize">{d.from.replace('_',' ')} → {d.to.replace('_',' ')}</span>
                <span className="text-red-600 font-semibold">-{d.dropped} <span className="text-slate-500">({d.dropped_pct}%)</span></span>
              </div>
            ))}
          </div>
        </div>

        {/* TAT */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="analytics-tat">
          <h2 className="text-lg font-bold">Time to hire (days)</h2>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {[
              ['Applied → Offer sent', tat?.applied_to_offer_sent],
              ['Offer → Accepted', tat?.offer_sent_to_accepted],
              ['Applied → Joined', tat?.applied_to_joined],
              ['Applied → Rejected', tat?.applied_to_rejected],
            ].map(([label, v]) => (
              <div key={label} className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
                <div className="text-2xl font-extrabold text-slate-900 mt-1">{v?.avg_days ?? 0}<span className="text-sm text-slate-500 font-normal ml-1">days</span></div>
                <div className="text-xs text-slate-500 mt-1">n={v?.samples ?? 0}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sources */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="analytics-sources">
          <h2 className="text-lg font-bold">Source ROI</h2>
          <table className="w-full text-sm mt-3">
            <thead className="text-xs text-slate-500 uppercase">
              <tr><th className="text-left py-2">Source</th><th className="text-right">Apps</th><th className="text-right">Interviewed</th><th className="text-right">Offered</th><th className="text-right">Joined</th><th className="text-right">Conv %</th></tr>
            </thead>
            <tbody>
              {(sources?.sources || []).map((s) => (
                <tr key={s.source} className="border-t">
                  <td className="py-2 capitalize">{s.source.replace(/_/g,' ')}</td>
                  <td className="text-right font-semibold">{s.applications}</td>
                  <td className="text-right">{s.interviewed}</td>
                  <td className="text-right">{s.offered}</td>
                  <td className="text-right">{s.joined}</td>
                  <td className="text-right font-bold text-emerald-600">{s.conversion_pct}%</td>
                </tr>
              ))}
              {(!sources?.sources || sources.sources.length === 0) && <tr><td colSpan={6} className="text-center text-slate-500 py-6">No data yet.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Offers */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="analytics-offers">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Offer economics</h2>
            <div className="text-sm"><TrendingUp className="inline h-4 w-4 text-emerald-600 mr-1" /> Acceptance <b>{offers?.acceptance_rate_pct ?? 0}%</b></div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {['sent', 'accepted', 'rejected', 'expired', 'revoked', 'draft'].map((k) => (
              <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
                <div className="text-[10px] uppercase text-slate-500">{k}</div>
                <div className="text-lg font-bold text-slate-900">{offers?.by_status?.[k] ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Decline reasons</div>
            {(offers?.decline_reasons || []).slice(0, 6).map((r, i) => (
              <div key={i} className="text-xs py-1 border-t border-slate-100">
                <span className="text-slate-500">{r.offer_id}</span> · <span className="font-medium">{r.candidate}</span>
                <div className="text-slate-700 mt-0.5">"{r.reason}"</div>
              </div>
            ))}
            {(!offers?.decline_reasons || offers.decline_reasons.length === 0) && <div className="text-xs text-slate-500">No declines yet — nice work!</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
